/**
 * ToastContext — transient notification system.
 * Replaces window.alert everywhere in the app.
 *
 * Usage:
 *   const { toast } = useToast();
 *   toast('Message envoyé !', 'success');
 *   toast('Erreur réseau.', 'error');
 *
 * Variant values: 'success' | 'error' | 'info' | 'warning'
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(undefined);

let nextId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const toast = useCallback((message, variant = 'info', duration = 4000) => {
    const id = ++nextId;
    setToasts((prev) => [...prev, { id, message, variant }]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast, dismiss, toasts }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (ctx === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
};

export default ToastContext;
